import { useSWRCompat as useSWR } from "@/shared/lib/queryClient";
import { AuthType, VITE_CLOUD_ENABLED } from "@/shared/lib/constants";
import { SWR_KEYS } from "@/shared/lib/swr-keys";

interface AuthTypeAPIResponse {
  auth_type: string;
  requires_verification?: boolean;
  anonymous_user_enabled?: boolean | null;
  password_min_length?: number;
  has_users?: boolean;
  oauth_enabled?: boolean;
  // Actual field this backend's GET /api/auth/type sends (see
  // backend/services/api-gateway .../auth_handler.go AuthType) — kept
  // alongside oauth_enabled above since that's what this hook originally
  // expected (VNP Workplace upstream shape); neither is guaranteed present.
  google_enabled?: boolean;
}

export interface AuthTypeMetadata {
  authType: AuthType;
  autoRedirect: boolean;
  requiresVerification: boolean;
  anonymousUserEnabled: boolean | null;
  passwordMinLength: number;
  hasUsers: boolean;
  oauthEnabled: boolean;
  googleEnabled: boolean;
}

const DEFAULT_AUTH_TYPE_METADATA: AuthTypeMetadata = {
  authType: VITE_CLOUD_ENABLED ? AuthType.CLOUD : AuthType.BASIC,
  autoRedirect: false,
  requiresVerification: false,
  anonymousUserEnabled: null,
  passwordMinLength: 0,
  hasUsers: false,
  oauthEnabled: false,
  googleEnabled: false,
};

async function fetchAuthTypeMetadata(url: string): Promise<AuthTypeMetadata> {
  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to fetch auth type metadata");
  const data: AuthTypeAPIResponse = await res.json();
  const authType = VITE_CLOUD_ENABLED
    ? AuthType.CLOUD
    : (data.auth_type as AuthType);
  return {
    authType,
    autoRedirect: authType === AuthType.OIDC || authType === AuthType.SAML,
    requiresVerification: data.requires_verification ?? false,
    anonymousUserEnabled: data.anonymous_user_enabled ?? null,
    passwordMinLength: data.password_min_length ?? 0,
    hasUsers: data.has_users ?? false,
    oauthEnabled: data.oauth_enabled ?? false,
    googleEnabled: data.google_enabled ?? data.oauth_enabled ?? false,
  };
}

export function useAuthTypeMetadata(): {
  authTypeMetadata: AuthTypeMetadata;
  isLoading: boolean;
  error: Error | undefined;
} {
  const { data, error, isLoading } = useSWR<AuthTypeMetadata>(
    SWR_KEYS.authType,
    fetchAuthTypeMetadata,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      dedupingInterval: 30_000,
    }
  );

  return {
    authTypeMetadata: data ?? DEFAULT_AUTH_TYPE_METADATA,
    isLoading,
    error,
  };
}
